import {
  CarrierContact,
  CarrierInfraction,
  CarrierVerification,
  CarrierVerificationInfo,
} from 'types/CarrierVerification';
import { Maybe, Undef } from 'types/UtilityTypes';
import { EmailSearchResultCategory } from 'types/enums/EmailSearchResultCategory';
import { CarrierVerification as CarrierVerificationEnum } from 'types/enums/Integrations';
import { WarningType } from 'types/enums/WarningType';

export function getAvailableContacts(
  carrier: Maybe<CarrierVerification>
): CarrierContact[] {
  if (!carrier?.contacts) return [];
  return carrier.contacts.filter(
    (contact) => contact.email && contact.authorized_for_packets
  );
}

export function formatContactName(contact: CarrierContact): string {
  const name = `${contact.first_name} ${contact.last_name}`.trim();
  if (name) {
    return `${name} (${contact.email})`;
  }
  return contact.email;
}

export function getCarrierColors(carrier: Maybe<CarrierVerification>): {
  border: string;
  badge: string;
} {
  if (!carrier)
    return {
      border: 'border-neutral-300',
      badge: 'border-neutral-500 text-neutral-700 bg-neutral-50',
    };

  switch (carrier.integration_name) {
    case CarrierVerificationEnum.FreightValidate: {
      const rating = carrier.risk_rating.toLowerCase();
      switch (true) {
        case rating.includes('none'):
          return {
            border: 'border-error-500',
            badge: 'border-error-500 text-error-700 bg-error-50',
          };
        case rating.includes('warning'):
          return {
            border: 'border-warning-500',
            badge: 'border-warning-500 text-warning-700 bg-warning-50',
          };
        default:
          return {
            border: 'border-success-500',
            badge: 'border-success-500 text-success-700 bg-success-50',
          };
      }
    }
    case CarrierVerificationEnum.Highway: {
      // Colors follow the selected carrier's rating. Authorized-user is an
      // email-level badge and must not paint a green border over "Not Authorized".
      const highwayRating = carrier.risk_rating.toLowerCase();
      switch (true) {
        case highwayRating.includes('not authorized'):
          return {
            border: 'border-error-500',
            badge: 'border-error-500 text-error-700 bg-error-50',
          };
        case highwayRating.includes('review required'):
          return {
            border: 'border-warning-500',
            badge: 'border-warning-500 text-warning-700 bg-warning-50',
          };
        default:
          return {
            border: 'border-success-500',
            badge: 'border-success-500 text-success-700 bg-success-50',
          };
      }
    }
    case CarrierVerificationEnum.MyCarrierPortal: {
      const mcpRating = carrier.risk_rating.toLowerCase();
      switch (true) {
        case mcpRating.includes('unacceptablefail'):
          return {
            border: 'border-error-500',
            badge: 'border-error-500 text-error-700 bg-error-50',
          };
        case mcpRating.includes('unacceptablereview'):
          return {
            border: 'border-warning-500',
            badge: 'border-warning-500 text-warning-700 bg-warning-50',
          };
        default:
          return {
            border: 'border-accent-500',
            badge: 'border-accent-500 text-accent-700 bg-accent-50',
          };
      }
    }
    case CarrierVerificationEnum.Truckstop: {
      const truckstopRating = carrier.risk_rating.toLowerCase();
      switch (true) {
        case truckstopRating.includes('unauthorized'):
          return {
            border: 'border-error-500',
            badge: 'border-error-500 text-error-700 bg-error-50',
          };
        case truckstopRating.includes('unacceptablereview'):
          return {
            border: 'border-warning-500',
            badge: 'border-warning-500 text-warning-700 bg-warning-50',
          };
        default:
          return {
            border: 'border-error-600',
            badge: 'border-error-600 text-error-700 bg-error-50',
          };
      }
    }
    default:
      return {
        border: 'border-neutral-300',
        badge: 'border-neutral-500 text-neutral-700 bg-neutral-50',
      };
  }
}

export function getWarningType(warning: string): WarningType {
  const warningLower = warning.toLowerCase();
  if (
    warningLower.includes('fraud') ||
    warningLower.includes('identity theft') ||
    warningLower.includes('pretend') ||
    warningLower.includes('double broker')
  ) {
    return WarningType.Error;
  }
  if (
    warningLower.includes('identity') ||
    warningLower.includes('spoof') ||
    warningLower.includes('closely related')
  ) {
    return WarningType.Warning;
  }
  // Match "dispatch service" and "dispatcher-connected carriers".
  if (warningLower.includes('dispatch')) {
    return WarningType.Info;
  }
  return WarningType.Warning;
}

const WARNING_STYLES: Record<WarningType, { banner: string; text: string }> = {
  [WarningType.Error]: {
    banner: 'border-error-200 bg-error-50',
    text: 'text-error-600',
  },
  [WarningType.Warning]: {
    banner: 'border-warning-200 bg-warning-50',
    text: 'text-warning-600',
  },
  [WarningType.Info]: {
    banner: 'border-info-200 bg-info-50',
    text: 'text-info-600',
  },
};

export function getWarningStyles(warningType: WarningType): {
  banner: string;
  text: string;
} {
  return WARNING_STYLES[warningType];
}

export function getWarningBannerClassName(warningType: WarningType): string {
  return WARNING_STYLES[warningType].banner;
}

export function getWarningTextClassName(warningType: WarningType): string {
  return WARNING_STYLES[warningType].text;
}

/** Merge a Highway all_carriers entry onto the parent email-search result. */
export function mergeCarrierVerificationSubCarrier(
  parent: CarrierVerification,
  info: CarrierVerificationInfo
): CarrierVerification {
  return {
    ...parent,
    name: info.name,
    dba: info.dba,
    dot_number: info.dot_number.toString(),
    docket_number: info.docket_number,
    risk_rating: info.risk_rating,
    // Always take sub-carrier location (including missing) so a different
    // parent's city/state does not linger on the selected carrier.
    city: info.headquarters_city,
    state: info.headquarters_state,
    ...(info.carrier_id != null ? { carrier_id: info.carrier_id } : {}),
  };
}

/** Resolve the card's displayed carrier for the selected sub-carrier index. */
export function getDisplayCarrier(
  parent: Maybe<CarrierVerification>,
  selectedIndex: number
): Maybe<CarrierVerification> {
  if (!parent) return null;

  if (
    parent.all_carriers &&
    parent.all_carriers.length > 1 &&
    selectedIndex < parent.all_carriers.length
  ) {
    return mergeCarrierVerificationSubCarrier(
      parent,
      parent.all_carriers[selectedIndex]
    );
  }

  return parent;
}

export type EmailSearchResultMessage = {
  message: string;
  type: WarningType;
};

export function getEmailSearchResultMessage(
  category: Undef<string>
): Maybe<EmailSearchResultMessage> {
  if (!category) return null;

  const categoryLower = category.toLowerCase();

  if (
    categoryLower ===
      EmailSearchResultCategory.FoundEmailAssociatedWithOneCarrier ||
    categoryLower ===
      EmailSearchResultCategory.FoundEmailDomainAssociatedWithOneCarrier
  ) {
    return null;
  }

  if (categoryLower.includes(EmailSearchResultCategory.MultipleCarriers)) {
    return null;
  }

  if (categoryLower.includes(EmailSearchResultCategory.Fraud)) {
    return {
      message:
        'This email has been flagged for potential fraud. Please verify before proceeding.',
      type: WarningType.Error,
    };
  }

  if (categoryLower.includes(EmailSearchResultCategory.IdentityAlert)) {
    return {
      message:
        'This carrier has an identity alert. Please verify before proceeding.',
      type: WarningType.Warning,
    };
  }

  if (categoryLower.includes(EmailSearchResultCategory.SpoofingAttempt)) {
    return {
      message:
        'Potential email spoofing detected. Please verify the sender before proceeding.',
      type: WarningType.Warning,
    };
  }

  if (categoryLower.includes(EmailSearchResultCategory.DispatcherService)) {
    return {
      message: 'This email is associated with a dispatcher service.',
      type: WarningType.Info,
    };
  }

  return null;
}

export function getInfractionSeverityColor(severity: string): string {
  const severityLower = severity.toLowerCase();
  switch (true) {
    case severityLower === 'fail':
      return 'border-error-600 text-error-700 bg-error-50';
    case severityLower === 'review':
      return 'border-warning-500 text-warning-700 bg-warning-50';
    case severityLower === 'medium':
      return 'border-warning-500 text-warning-700 bg-warning-50';
    case severityLower === 'low':
      return 'border-accent-500 text-accent-700 bg-accent-50';
    case severityLower === 'acceptable':
      return 'border-success-500 text-success-700 bg-success-50';
    default:
      return 'border-neutral-500 text-neutral-700 bg-neutral-50';
  }
}

export function getInfractionSeverityOrder(severity: string): number {
  const severityLower = severity.toLowerCase();
  switch (true) {
    case severityLower === 'fail':
      return 0;
    case severityLower === 'review':
      return 1;
    case severityLower === 'medium':
      return 2;
    case severityLower === 'low':
      return 3;
    case severityLower === 'acceptable':
      return 4;
    default:
      return 5;
  }
}

export function sortInfractionsBySeverity(
  infractions: CarrierInfraction[]
): CarrierInfraction[] {
  return [...infractions].sort(
    (a, b) =>
      getInfractionSeverityOrder(a.severity) -
      getInfractionSeverityOrder(b.severity)
  );
}
